// Paleontology Agent Types

export interface PaleontologyStartRequest {
  initialPrompt?: string;
  sessionType?: string;
  model?: string;
  temperature?: number;
  maxTokens?: number;
}

export interface PaleontologyStartResponse {
  success: boolean;
  errors?: string[];
  sessionId?: string;
  visualization?: PaleontologyVisualization;
}

export interface PaleontologyIterationRequest {
  agentType?: string;
  detailDomain?: string;
  customInstructions?: string;
  model?: string;
  temperature?: number;
  maxTokens?: number;
}

export interface PaleontologyIterationResponse {
  success: boolean;
  errors?: string[];
  visualization?: PaleontologyVisualization;
}

export interface PaleontologyVisualization {
  title: string;
  era: string;
  period: string;
  epoch?: string;
  timeRange: string;
  description: string;
  keyFeatures: string[];
  content: PaleontologyContent;
  timestamp: string;
}

export interface PaleontologyContent {
  overview: string;
  geologicalContext: GeologicalContext;
  lifeforms: LifeformDetails;
  environment: EnvironmentalConditions;
  fossilRecord: FossilRecord;
  majorEvents: string[];
  significance: string[];
  modernConnections?: string[];
}

export interface GeologicalContext {
  tectonicActivity: string;
  continentalConfiguration: string;
  rockFormations: string[];
  mineralDeposits?: string[];
}

export interface LifeformDetails {
  dominantSpecies: Species[];
  emergingSpecies: Species[];
  extinctSpecies?: Species[];
  evolutionaryInnovations: string[];
}

export interface Species {
  name: string;
  type: string;
  characteristics: string[];
  significance?: string;
}

export interface EnvironmentalConditions {
  climate: string;
  atmosphere: string;
  oceanConditions?: string;
  vegetationType?: string;
  ecosystems: string[];
}

export interface FossilRecord {
  notableFossils: Fossil[];
  preservationTypes: string[];
  fossilSites?: string[];
}

export interface Fossil {
  name: string;
  type: string;
  location?: string;
  significance: string;
}

// Session management
export interface PaleontologySession {
  id: string;
  sessionId: string;
  agentType: PaleontologyAgentType;
  initialPrompt: string;
  visualizations: PaleontologyVisualization[];
  createdAt: string;
  updatedAt: string;
}

export type PaleontologyAgentType = 
  | 'cenozoic'
  | 'mesozoic'
  | 'paleozoic'
  | 'precambrian'
  | 'paleoecosystem';

export interface PaleontologyAgentInfo {
  id: PaleontologyAgentType;
  name: string;
  description: string;
  icon: string;
  timeRange: string;
  keyPeriods: string[];
  majorEvents: string[];
}

export const PALEONTOLOGY_AGENTS: PaleontologyAgentInfo[] = [
  {
    id: 'precambrian',
    name: 'Precambrian Era',
    description: 'Explore Earth\'s earliest history, from formation to the dawn of complex life',
    icon: '🌍',
    timeRange: '4.6 billion - 541 million years ago',
    keyPeriods: ['Hadean', 'Archean', 'Proterozoic'],
    majorEvents: [
      'Earth formation',
      'First life emergence',
      'Great Oxidation Event',
      'Snowball Earth',
      'Ediacaran fauna'
    ]
  },
  {
    id: 'paleozoic',
    name: 'Paleozoic Era',
    description: 'Study the explosion of life, from marine invertebrates to the first land vertebrates',
    icon: '🦎',
    timeRange: '541 - 252 million years ago',
    keyPeriods: ['Cambrian', 'Ordovician', 'Silurian', 'Devonian', 'Carboniferous', 'Permian'],
    majorEvents: [
      'Cambrian Explosion',
      'First land plants',
      'Age of Fishes',
      'Coal forests',
      'Permian-Triassic extinction'
    ]
  },
  {
    id: 'mesozoic',
    name: 'Mesozoic Era',
    description: 'The Age of Dinosaurs - explore the rise and fall of Earth\'s most famous prehistoric creatures',
    icon: '🦕',
    timeRange: '252 - 66 million years ago',
    keyPeriods: ['Triassic', 'Jurassic', 'Cretaceous'],
    majorEvents: [
      'Rise of dinosaurs',
      'First mammals',
      'First birds',
      'Flowering plants evolution',
      'K-Pg extinction event'
    ]
  },
  {
    id: 'cenozoic',
    name: 'Cenozoic Era',
    description: 'The Age of Mammals - from the dinosaur extinction to the rise of humans',
    icon: '🦣',
    timeRange: '66 million years ago - present',
    keyPeriods: ['Paleogene', 'Neogene', 'Quaternary'],
    majorEvents: [
      'Mammal diversification',
      'Rise of primates',
      'Human evolution',
      'Ice ages',
      'Modern ecosystems'
    ]
  },
  {
    id: 'paleoecosystem',
    name: 'Paleoecosystem Analysis',
    description: 'Reconstruct ancient ecosystems and understand prehistoric environmental interactions',
    icon: '🌿',
    timeRange: 'All geological time',
    keyPeriods: ['Cross-era analysis'],
    majorEvents: [
      'Ecosystem evolution',
      'Food web reconstruction',
      'Climate interactions',
      'Mass extinctions',
      'Recovery patterns'
    ]
  }
];